import type { PeaksSettings, SourceType } from '../../../../../../types';

const MIN_PEAK_VALUE = 0.00001;

const PLAYER_WAVEFORM_SETTINGS = {
  podcastPlayer: {
    smoothness: 0.5,
    compression: 2.5,
    sensitivity: 1.5,
    minHeight: 0.05
  },
  audioPlayer: {
    smoothness: 1.0,
    compression: 10.0,
    sensitivity: 1.0,
    minHeight: 0.05
  }
};

const decodeSettingsBySourceType: Partial<Record<SourceType, PeaksSettings>> = {
  rss: PLAYER_WAVEFORM_SETTINGS.podcastPlayer,
  player: PLAYER_WAVEFORM_SETTINGS.audioPlayer
};

const decodeBase64ToBytes = (encoded: string) => {
  try {
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
  } catch {
    return new Uint8Array(0);
  }
};

const bytesToNormalizedPeaks = (bytes: Uint8Array) => {
  const peaks = new Float32Array(bytes.length);
  for (let i = 0; i < peaks.length; i++) {
    peaks[i] = bytes[i] / 255;
  }

  return peaks;
};

const normalizeAndCompress = (
  peaks: Float32Array,
  compression: number,
  sensitivity: number
) => {
  const max = Math.max(...peaks, MIN_PEAK_VALUE);

  return peaks.map((p) => Math.pow(p / max, compression) * sensitivity);
};

const applyWeightedSmoothing = (data: Float32Array, smoothness: number) => {
  const windowSize = Math.floor(data.length * 0.01 * smoothness) || 2;
  const smoothed = new Float32Array(data.length);

  for (let i = 0; i < data.length; i++) {
    let sum = 0;
    let count = 0;

    for (let j = -windowSize; j <= windowSize; j++) {
      const idx = i + j;
      if (idx >= 0 && idx < data.length) {
        const weight = 1 - Math.abs(j) / (windowSize + 1);
        sum += data[idx] * weight;
        count += weight;
      }
    }

    smoothed[i] = sum / count;
  }

  return smoothed;
};

const applyExponentialSmoothing = (data: Float32Array, smoothness: number) => {
  const final = new Float32Array(data.length);
  final[0] = data[0];

  for (let i = 1; i < data.length; i++) {
    final[i] = data[i] * smoothness + final[i - 1] * (1 - smoothness);
  }

  return final;
};

const applyMinimumHeight = (peaks: Float32Array, minHeight: number) => {
  for (let i = 0; i < peaks.length; i++) {
    peaks[i] = Math.max(peaks[i], minHeight);
  }

  return peaks;
};

export const decodePeaks = (
  encoded: string,
  sourceType: SourceType,
  peaksSettings?: PeaksSettings
) => {
  const settings =
    peaksSettings ??
    decodeSettingsBySourceType[sourceType] ??
    PLAYER_WAVEFORM_SETTINGS.audioPlayer;

  const { smoothness, compression, sensitivity, minHeight } = settings;

  const bytes = decodeBase64ToBytes(encoded);

  if (bytes.length === 0) {
    throw new Error('Failed to decode peaks data');
  }

  const peaks = bytesToNormalizedPeaks(bytes);
  const compressed = normalizeAndCompress(peaks, compression, sensitivity);
  const smoothed = applyWeightedSmoothing(compressed, smoothness);
  const exponentiallySmoothed = applyExponentialSmoothing(smoothed, smoothness);
  const final = applyMinimumHeight(exponentiallySmoothed, minHeight);

  return [final];
};
